import React, { useState } from "react";
import styled, { css } from "styled-components";
import * as styles from "../../../styleguide/styles/Stylesheet";
import { Body, Header } from "../../../styleguide/atoms/index";
import Expand from "../../../media/icons/expand";
import Collapse from "../../../media/icons/collapse";

const CharacterContainer = styled.div`
  position: relative;
  flex: 0 1 320px;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 10px;
  box-shadow: ${styles.box_shadow};
  padding: ${styles.spacing_standard} ${styles.spacing_standard} 0;
  background-color: ${styles.color_white};
  border: 4px solid ${styles.color_white};

  ${({ selected }) =>
    selected &&
    css`
      border: 4px solid ${styles.color_primary};
    `}

  ${({ listView }) =>
    listView &&
    css`
      flex: none;
      width: 100%;
      max-width: ${styles.width_md};
      flex-direction: row;
      padding: ${styles.spacing_standard};
      gap: ${styles.spacing_standard_half};
      @media only screen and (max-width: ${styles.width_sm}) {
        padding: ${styles.spacing_standard_half};
      }
    `}

  ${({ collapsed }) =>
    collapsed &&
    css`
      flex: none;
      width: 100%;
      max-width: 320px;
      flex-direction: row;
      padding: ${styles.spacing_standard};
      gap: ${styles.spacing_standard_half};
      @media only screen and (max-width: ${styles.width_sm}) {
        padding: ${styles.spacing_standard_half};
      }
    `}

  ${({ onClick }) =>
    onClick &&
    css`
      cursor: pointer;
    `}
`;

const TagsWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  gap: ${styles.spacing_standard_half};

  ${({ listView }) =>
    listView &&
    css`
      gap: 0;
      width: auto;
    `}
`;

const CharacterTag = styled.div`
  margin-bottom: 10px;
  border-radius: 5px;
  padding: 6px 8px;
  font-size: 14px;
  font-family: ${styles.font_sans};
  font-weight: ${styles.font_bold};
  color: ${styles.color_white};
  text-transform: uppercase;
  background-color: ${(p) => p.$bg || styles.color_white};

  ${({ listView }) =>
    listView &&
    css`
      margin-bottom: 0px;
    `}
`;

const CharacterImage = styled.img`
  width: 100%;
  overflow: hidden;
  border-radius: 10px;
  margin-bottom: 20px;
  object-fit: cover;
`;

const ToggleWrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: -18px;
  right: -18px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${styles.color_white};
  box-shadow: ${styles.box_shadow};
  cursor: pointer;
`;

export function CharacterCard({
  image,
  role,
  status,
  title,
  description,
  selected = false,
  onClick,
  listView = false,
  collapsable = false,
}) {
  const [collapsed, setIsCollapsed] = useState(listView);
  const roleToBg = {
    Camper: styles.color_green,
    Villain: styles.color_red,
    Unique: styles.color_purple,
  };
  const bg = roleToBg[role] ?? styles.color_white;

  const statusToBg = {
    Alive: styles.color_primary,
    Dead: styles.color_black,
    Inactive: styles.color_gry_2,
  };
  const statusBg = statusToBg[status] ?? styles.color_white;

  return (
    <CharacterContainer
      selected={selected}
      listView={listView}
      onClick={onClick}
      collapsed={collapsed}
    >
      {collapsable ? (
        <>
          {collapsed ? (
            <ToggleWrapper onClick={() => setIsCollapsed(!collapsed)}>
              <Expand
                color={styles.color_accent}
                width={16}
                strokeWidth={2.5}
                aria-hidden="true"
              />
            </ToggleWrapper>
          ) : (
            <ToggleWrapper onClick={() => setIsCollapsed(!collapsed)}>
              <Collapse
                color={styles.color_accent}
                width={16}
                strokeWidth={2.5}
                aria-hidden="true"
              />
            </ToggleWrapper>
          )}
        </>
      ) : null}
      {collapsed ? (
        <>
          <Header type="h5" align="center" padding={0}>
            HIDDEN
          </Header>
        </>
      ) : (
        <>
          {image && !listView ? (
            <CharacterImage src={image} alt={title} />
          ) : null}
          <TagsWrapper listView={listView}>
            {role ? (
              <CharacterTag $bg={bg} listView={listView}>
                {role}
              </CharacterTag>
            ) : null}
            {!listView && status ? (
              <CharacterTag $bg={statusBg} listView={listView}>
                {status}
              </CharacterTag>
            ) : null}
          </TagsWrapper>
          {listView ? (
            <Body>
              <b>{title}</b>
            </Body>
          ) : (
            <Header type={listView ? "h4" : "h2"} align="center">
              {title}
            </Header>
          )}
          {description && !listView ? (
            <Body align="center">{description}</Body>
          ) : null}
          {listView ? (
            <CharacterTag $bg={statusBg} listView={listView}>
              {status}
            </CharacterTag>
          ) : null}
        </>
      )}
    </CharacterContainer>
  );
}
